import type { FC } from "react";
import { cva } from "@stubhub/broadway-foundation/css";
import type { CategoryGridItem } from "src/http/types";
import { SiteErrorBoundary } from "src/shared/components/SiteErrorBoundary";
import { useAppContext } from "src/shared/context/AppContext";
import { BoundaryNames } from "src/shared/utils/sentry/BoundaryNames";
import { SentrySeverityLevel } from "src/shared/utils/sentry/SentrySeverityLevel";
import { AlternativePerformerNewGrid } from "src/website-apps/responsive/stubhub-performer/components/alternative-performer-new-grid/AlternativePerformerNewGrid";
import { TOP_LEVEL_CATEGORY_IDS } from "src/website-apps/shared/components/explore-events/constants";
import { LoadingCards } from "src/website-apps/shared/components/LoadingCards";

const relativeWrapperRecipe = cva({
  base: {
    marginBlockStart: "primitives.200",
    marginBlockEnd: "primitives.200",
    position: "relative",
  },
});

interface EitherSimilarTeamsProps {
  altPerformerItems?: CategoryGridItem[];
  clientApplicationId: Maybe<number>;
  categoryId?: number | undefined;
}

const EitherSimilarTeams: FC<EitherSimilarTeamsProps> = ({ altPerformerItems, clientApplicationId, categoryId }) => {
  if (clientApplicationId !== 3) {
    return (
      <div>
        <AlternativePerformerNewGrid
          // "Similar Teams"
          titleContentId="cid$#1812270#"
          items={altPerformerItems?.slice(0, 9) ?? []}
          categoryId={categoryId}
        />
      </div>
    );
  }

  return (
    <div className={relativeWrapperRecipe()}>
      <AlternativePerformerNewGrid
        // "Similar Teams"
        titleContentId="cid$#1812270#"
        items={altPerformerItems?.slice(0, 9) ?? []}
        categoryId={categoryId}
      />
    </div>
  );
};

interface Props {
  altPerformerItems?: CategoryGridItem[];
  isLoading?: boolean;
  categoryId?: number | undefined;
}

export const AlternativePerformers: FC<Props> = ({ altPerformerItems, isLoading, categoryId }) => {
  const appContext = useAppContext();
  const isSports = appContext.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.SPORT;

  if (!altPerformerItems?.length && !isLoading) {
    return null;
  }
  if (isLoading) {
    return <LoadingCards isSimilar />;
  }
  return (
    <SiteErrorBoundary boundaryName={BoundaryNames.ALTERNATIVE_PERFORMERS} severity={SentrySeverityLevel.WARNING}>
      {isSports ? (
        <EitherSimilarTeams
          altPerformerItems={altPerformerItems ?? []}
          clientApplicationId={appContext.clientApplicationId}
          categoryId={categoryId}
        />
      ) : (
        <AlternativePerformerNewGrid items={altPerformerItems ?? []} categoryId={categoryId} />
      )}
    </SiteErrorBoundary>
  );
};
